import { useState } from "react";
import { useForm } from "react-hook-form";
import { message } from "antd";
import {
  useCreateEvaluationQuestion,
  useQuestionsByForm,
} from "./logic/useEvaluationQuestion";
import { useSkillsList } from "./logic/useEvaluationQuestion";
import "./EvaluationQuestionForm.scss";
import { Select } from "antd";

const EvaluationQuestionForm = ({ sectionId, formId, onSuccess }) => {
  const [showForm, setShowForm] = useState(false);
  const [inputType, setInputType] = useState("text");
  const [options, setOptions] = useState([]);
  const [optionInput, setOptionInput] = useState("");
  const { data: skillsData, isLoading: skillsLoading } = useSkillsList();
  const skills = Array.isArray(skillsData) ? skillsData : [];
  const [branching, setBranching] = useState({});
  const [enableBranching, setEnableBranching] = useState(false);

  const { data: allQuestions = [] } = useQuestionsByForm(formId);
  const [selectedSkillId, setSelectedSkillId] = useState(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  const { mutate, isLoading } = useCreateEvaluationQuestion();

  const addOption = () => {
    if (optionInput.trim()) {
      setOptions([...options, optionInput.trim()]);
      setOptionInput("");
    }
  };

  const removeOption = (index) => {
    setOptions(options.filter((_, i) => i !== index));
  };

  const onSubmit = (data) => {
    if (!formId || !sectionId) {
      message.error("Missing formId or sectionId..");
      return;
    }

    const isSkillLinked = data.input_type === "scale" && selectedSkillId;

    const isOptionsInput =
      ["radio", "checkbox"].includes(data.input_type) &&
      options.length > 0 &&
      options.every((opt) => typeof opt === "string" && opt.trim() !== "");

    const payload = {
      text: data.text,
      input_type: data.input_type,
      is_required: !!data.is_required,
      sectionId,
      formId,
      skill_id: isSkillLinked ? selectedSkillId : null,
      options: isSkillLinked
        ? [1, 2, 3, 4]
        : isOptionsInput
        ? options
        : undefined,
      branching:
        enableBranching && Object.keys(branching).length > 0
          ? branching
          : undefined,
    };

    mutate(payload, {
      onSuccess: () => {
        message.success("Question added !");
        reset();
        setInputType("text");
        setOptions([]);
        setOptionInput("");
        setSelectedSkillId(null);
        setBranching({});
        setEnableBranching(false);
        setShowForm(false);
        onSuccess?.();
      },
      onError: (err) => {
        message.error(err?.response?.data?.message || "Failed to add question");
      },
    });
  };

  const handleCancel = () => {
    reset();
    setInputType("text");
    setOptions([]);
    setOptionInput("");
    setShowForm(false);
  };

  if (!showForm) {
    return (
      <div className="add-question-button-container">
        <button
          className="add-question-button"
          onClick={() => setShowForm(true)}
          type="button"
        >
          <span className="add-icon">+</span>
          Add New Question
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="editable-form">
      <label>Question</label>
      <input
        className="input-standard"
        placeholder="Enter your question"
        {...register("text", { required: "Text is required" })}
      />
      {errors.text && <p className="error">{errors.text.message}</p>}

      <label>Type</label>
      <select
        {...register("input_type")}
        onChange={(e) => {
          setInputType(e.target.value);
          setOptions([]);
        }}
        className="input-standard"
      >
        <option value="text">Text</option>
        <option value="scale">Skill Level (1–4)</option>
        <option value="radio">Radio</option>
        <option value="checkbox">Checkbox</option>
      </select>
      {inputType === "scale" && (
        <>
          <label>Related Skill</label>
          <Select
            showSearch
            placeholder="Select a skill"
            optionFilterProp="label"
            value={selectedSkillId}
            onChange={(value) => setSelectedSkillId(value)}
            loading={skillsLoading}
            className="input-standard"
          >
            {skills.map((skill) => (
              <Select.Option key={skill.id} value={skill.id} label={skill.name}>
                {skill.name}
              </Select.Option>
            ))}
          </Select>
        </>
      )}
      {["radio", "checkbox"].includes(inputType) && (
        <>
          <label>Options</label>
          <div className="options-editor">
            <div>
              <input
                type="text"
                value={optionInput}
                onChange={(e) => setOptionInput(e.target.value)}
                placeholder="Add option"
              />
              <button type="button" onClick={addOption}>
                +
              </button>
            </div>
          </div>
          <div className="branching-toggle">
            {!enableBranching ? (
              <button
                type="button"
                onClick={() => setEnableBranching(true)}
                className="branching-button branching-add"
              >
                + Add branching logic
              </button>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setEnableBranching(false);
                  setBranching({});
                }}
                className="branching-button branching-cancel"
              >
                ✖ Cancel branching
              </button>
            )}
          </div>
        </>
      )}

      <label>
        <input type="checkbox" {...register("is_required")} /> Required
      </label>
     
      <ul className="option-list">
        {options.map((opt, i) => (
          <li key={i} className="option-item">
            <div className="option-row">
              <input
                type="text"
                value={opt}
                onChange={(e) => {
                  const updated = [...options];
                  updated[i] = e.target.value;
                  setOptions(updated);
                }}
                className="input-standard"
              />
              <span
                className="remove-option"
                onClick={() => removeOption(i)}
                title="Remove option"
              >
                ×
              </span>
            </div>

            {enableBranching && (
              <Select
                placeholder="Show question if selected"
                value={branching[opt] || undefined}
                onChange={(value) =>
                  setBranching((prev) => ({ ...prev, [opt]: value }))
                }
                style={{ width: "100%", marginTop: "0.5rem" }}
              >
                {Array.isArray(allQuestions) &&
                  allQuestions
                    .filter((q) => q.text && q.id !== formId)
                    .map((q) => (
                      <Select.Option key={q.id} value={q.id}>
                        {q.text}
                      </Select.Option>
                    ))}
              </Select>
            )}
          </li>
        ))}
      </ul>

      <div className="form-actions">
        <button type="button" className="cancel-button" onClick={handleCancel}>
          Cancel
        </button>
        <button
          type="submit"
          className="action-button"
          disabled={
            isLoading ||
            (["scale", "radio", "checkbox"].includes(inputType) &&
              options.some((o) => !o.trim()))
          }
        >
          {isLoading ? "Saving..." : "Save"}
        </button>
      </div>
    </form>
  );
};

export default EvaluationQuestionForm;
